import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, ResponsiveTable, TableSkeleton, EmptyState, ReasonModal, Modal, Drawer, SectionCard, AutoBadge, DateCell } from "@/admin/ui";
import { Plus, GitBranch, Undo2, Play, RefreshCw } from "lucide-react";

export default function AdminPlansV2() {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [drawer, setDrawer] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [np, setNp] = React.useState({ tier: "", display_name: "", price_monthly_cents: 0, monthly_requests: 0, max_api_keys: 1, rate_limit_per_second: 0, credit_allocation: 0, trial_days: 0 });
  const [reasonModal, setReasonModal] = React.useState(null);
  const [draftModal, setDraftModal] = React.useState(null);
  const [snapshotText, setSnapshotText] = React.useState('{"price_monthly_cents": 5000}');

  const load = React.useCallback(async () => {
    const r = await api.get("/api/admin/v2/plans", { params: { per_page: 100 } });
    setItems(r.data.items); setTotal(r.data.total);
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const openDrawer = async (id) => {
    const [lin, versions] = await Promise.all([
      api.get(`/api/admin/v2/plans/${id}`).then((r) => r.data),
      api.get(`/api/admin/v2/plans/${id}/versions`).then((r) => r.data.items),
    ]);
    setDrawer({ lin, versions });
  };

  const createLineage = async () => {
    if (!np.tier || !np.display_name) return toast.error(t("adminp.plans.tier_name_required"));
    try {
      await api.post("/api/admin/v2/plans", np);
      toast.success(t("adminp.plans.created")); setCreating(false); load();
      setNp({ tier: "", display_name: "", price_monthly_cents: 0, monthly_requests: 0, max_api_keys: 1, rate_limit_per_second: 0, credit_allocation: 0, trial_days: 0 });
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const publishVersion = (id, version) => setReasonModal({
    title: t("adminp.plans.publish_reason_prompt"), actionLabel: t("adminp.products.publish"),
    extras: <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-xs text-white/70">
      This creates a new published version. Existing subscribers keep their snapshot; new subscribers get this version at the effective date.
    </div>,
    onSubmit: async (reason) => {
      await api.post(`/api/admin/v2/plans/${id}/publish`, { version, reason });
      toast.success(t("common.saved")); load(); openDrawer(id);
    },
  });
  const rollback = (id, toVersion) => setReasonModal({
    title: t("adminp.plans.rollback_reason_prompt"), actionLabel: "Rollback", danger: true,
    extras: <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100">
      Rollback does not mutate the current version. It creates a new version whose snapshot equals the selected historical one.
    </div>,
    onSubmit: async (reason) => {
      await api.post(`/api/admin/v2/plans/${id}/rollback`, { to_version: toVersion, reason });
      toast.success(t("common.saved")); load(); openDrawer(id);
    },
  });
  const draftVersion = (id) => setDraftModal({ id });
  const submitDraft = async (reason) => {
    let snapshot;
    try { snapshot = JSON.parse(snapshotText); } catch { toast.error("Invalid JSON"); return; }
    await api.post(`/api/admin/v2/plans/${draftModal.id}/versions`, { reason, snapshot });
    toast.success(t("common.saved")); openDrawer(draftModal.id);
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-v2">
      <PageHeader title={t("adminp.plans.title")} description={t("adminp.plans.subtitle")}
        primary={<button className="lp-btn-primary text-xs py-1.5 px-3" onClick={() => setCreating(true)} data-testid="plv2-new"><Plus className="w-3.5 h-3.5" /> {t("adminp.plans.new")}</button>}
        secondary={<button className="lp-btn-ghost" onClick={load} aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>} />

      {!items ? <TableSkeleton rows={6} cols={6} /> : items.length === 0 ? (
        <EmptyState title={t("adminp.plans.empty")} />
      ) : (
        <ResponsiveTable minWidth={900}>
          <thead className="lp-table-sticky"><tr>
            <th>{t("adminp.plans.col.tier")}</th>
            <th>{t("adminp.plans.col.name")}</th>
            <th>{t("adminp.plans.col.status")}</th>
            <th>{t("adminp.plans.col.version")}</th>
            <th className="text-right">{t("adminp.plans.col.price")}</th>
            <th>{t("adminp.plans.col.effective")}</th>
          </tr></thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="cursor-pointer hover:bg-white/5" onClick={() => openDrawer(l.id)} data-testid={`plv2-row-${l.id.slice(0, 6)}`}>
                <td className="font-mono text-white/85 text-xs">{l.tier}</td>
                <td className="text-white/85">{l.display_name}</td>
                <td><AutoBadge value={l.status} /></td>
                <td className="text-white/70 tabular-nums">v{l.current_version || "—"}</td>
                <td className="text-right text-white/70 tabular-nums">${((l.current_version_snapshot?.price_monthly_cents || 0) / 100).toFixed(2)}</td>
                <td><DateCell value={l.current_version_effective_at} /></td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
      <div className="text-xs text-white/40">{total} {t("adminp.plans.total")}</div>

      <Modal open={creating} onClose={() => setCreating(false)} title={t("adminp.plans.new")} size="lg"
        footer={<>
          <button className="lp-btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
          <button className="lp-btn-primary" onClick={createLineage} data-testid="plv2-new-submit">{t("common.create")}</button>
        </>}>
        <div className="grid grid-cols-2 gap-2">
          <input className="lp-input" placeholder="tier" value={np.tier} onChange={(e) => setNp({ ...np, tier: e.target.value })} data-testid="plv2-new-tier" data-autofocus />
          <input className="lp-input" placeholder="Display name" value={np.display_name} onChange={(e) => setNp({ ...np, display_name: e.target.value })} data-testid="plv2-new-name" />
          <input className="lp-input" type="number" placeholder="Price (cents)" value={np.price_monthly_cents} onChange={(e) => setNp({ ...np, price_monthly_cents: parseInt(e.target.value || 0) })} />
          <input className="lp-input" type="number" placeholder="Monthly requests" value={np.monthly_requests} onChange={(e) => setNp({ ...np, monthly_requests: parseInt(e.target.value || 0) })} />
          <input className="lp-input" type="number" placeholder="Max API keys" value={np.max_api_keys} onChange={(e) => setNp({ ...np, max_api_keys: parseInt(e.target.value || 0) })} />
          <input className="lp-input" type="number" placeholder="Rate/sec" value={np.rate_limit_per_second} onChange={(e) => setNp({ ...np, rate_limit_per_second: parseInt(e.target.value || 0) })} />
          <input className="lp-input" type="number" placeholder="Credit allocation" value={np.credit_allocation} onChange={(e) => setNp({ ...np, credit_allocation: parseInt(e.target.value || 0) })} />
          <input className="lp-input" type="number" placeholder="Trial days" value={np.trial_days} onChange={(e) => setNp({ ...np, trial_days: parseInt(e.target.value || 0) })} />
        </div>
      </Modal>

      <Drawer open={!!drawer} onClose={() => setDrawer(null)} size="lg"
        subtitle={drawer?.lin.tier} title={drawer?.lin.display_name || ""} testid="plv2-drawer">
        {drawer && <>
          <div className="flex items-center gap-2 flex-wrap">
            <AutoBadge value={drawer.lin.status} />
            <span className="text-xs text-white/50">v{drawer.lin.current_version || "—"}</span>
            <button className="lp-btn-secondary text-xs ml-auto" onClick={() => draftVersion(drawer.lin.id)} data-testid="plv2-draft"><Plus className="w-3.5 h-3.5" /> {t("adminp.plans.draft_version")}</button>
          </div>
          <SectionCard title={<span className="flex items-center gap-1"><GitBranch className="w-3.5 h-3.5" /> {t("adminp.plans.versions")}</span>}>
            <ul className="space-y-2 text-xs">
              {drawer.versions.map((v) => (
                <li key={v.id} className="border-t border-white/5 pt-2 flex justify-between items-center gap-2">
                  <div>
                    <div className="text-white/90 flex items-center gap-2">v{v.version} <AutoBadge value={v.state} /></div>
                    <div className="text-white/40 text-[10px]"><DateCell value={v.created_at} /> · price ${((v.snapshot?.price_monthly_cents || 0) / 100).toFixed(2)}</div>
                  </div>
                  <div className="flex gap-1">
                    {v.state === "draft" && <button className="lp-btn-primary text-xs py-1 px-2" onClick={() => publishVersion(drawer.lin.id, v.version)} data-testid={`plv2-publish-${v.version}`} aria-label="Publish"><Play className="w-3 h-3" /></button>}
                    <button className="lp-btn-secondary text-xs py-1 px-2" onClick={() => rollback(drawer.lin.id, v.version)} data-testid={`plv2-rollback-${v.version}`} aria-label="Rollback"><Undo2 className="w-3 h-3" /></button>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
        </>}
      </Drawer>

      <ReasonModal open={!!reasonModal} {...(reasonModal || {})} onClose={() => setReasonModal(null)} onSubmit={reasonModal?.onSubmit || (async () => {})} />
      <Modal open={!!draftModal} title={t("adminp.plans.draft_version")} description={t("adminp.plans.draft_snapshot_prompt")} onClose={() => setDraftModal(null)} testid="plv2-draft-modal"
        footer={<>
          <button className="lp-btn-secondary" onClick={() => setDraftModal(null)}>Cancel</button>
          <button className="lp-btn-primary" onClick={() => setReasonModal({ title: t("adminp.plans.draft_reason_prompt"), actionLabel: "Create draft", onSubmit: async (r) => { await submitDraft(r); setDraftModal(null); } })} data-testid="plv2-draft-continue">Continue</button>
        </>}>
        <textarea className="lp-input w-full h-40 font-mono text-xs" value={snapshotText} onChange={(e) => setSnapshotText(e.target.value)} data-testid="plv2-draft-json" />
      </Modal>
    </div>
  );
}
